import React, { useState, useEffect, Suspense, lazy, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import OwnerInviteMemberModal from '../../components/modals/InviteMemberModal';
import AdminInviteMemberModal from '../team-members/components/InviteMemberModal';
import dataLoadingService from '../../utils/dataLoadingService.js';
import logoutService from '../../utils/logoutService.js';
import urlStateService from '../../utils/urlStateService.js';
import realApiService from '../../utils/realApiService';
import { getProgressFromStatus } from '../../utils/taskProgress';
import useToast from '../../hooks/useToast';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { generateProjectExportHtml } from '../../utils/exportHelpers';

// Lazy load the meeting scheduler modal
const MeetingScheduler = lazy(() => import('../../components/modals/components/MeetingScheduler'));

const tabs = [
  {
    id: 'overview',
    label: 'Overview',
    icon: 'LayoutDashboard',
    description: 'Project summary and key metrics',
    component: lazy(() => import('./components/ProjectOverview')),
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: 'CheckSquare',
    description: 'Task management and tracking',
    component: lazy(() => import('./components/TasksTab')),
  },

  {
    id: 'team',
    label: 'Team',
    icon: 'Users',
    description: 'Project team member management',
    component: lazy(() => import('./components/TeamTab')),
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'Settings',
    description: 'Project configuration and permissions',
    component: lazy(() => import('./components/SettingsTab')),
  },
];

const ProjectManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [showMeetingScheduler, setShowMeetingScheduler] = useState(false);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('member');
  const [canCreateProjects] = useState(true); // setCanCreateProjects removed (unused)
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState(null);

  // Get projectId and tab from URL using React Router hooks for more reliable detection
  const [projectId, setProjectId] = useState(searchParams.get('id') || urlStateService.getProjectId());
  const [tabFromUrl, setTabFromUrl] = useState(searchParams.get('tab') || urlStateService.getTab());
  const componentId = 'project-management';

  console.log('ProjectManagement: projectId from URL:', projectId, 'tab:', tabFromUrl);
  console.log('ProjectManagement: searchParams:', Object.fromEntries(searchParams.entries()));
  console.log('ProjectManagement: location:', location.pathname, location.search);

  // Redirect from workflow to overview if needed (since workflow tab is removed)
  useEffect(() => {
    if (activeTab === 'workflow') {
      setActiveTab('overview');
      urlStateService.setTab('overview');
    }
  }, [activeTab]);

  // Filter tabs based on user role - hide Settings for members
  const visibleTabs = useMemo(() => {
    if (userRole === 'member') {
      return tabs.filter(tab => tab.id !== 'settings');
    }
    return tabs;
  }, [userRole]);

  // Use refs to track latest state for use in callbacks without stale closures
  const latestProjectId = useRef(projectId);
  const latestTabFromUrl = useRef(tabFromUrl);

  // Update refs when state changes
  useEffect(() => {
    latestProjectId.current = projectId;
  }, [projectId]);

  useEffect(() => {
    latestTabFromUrl.current = tabFromUrl;
  }, [tabFromUrl]);

  // Create memoized data loading function
  const loadProjectData = useCallback(async (forceProjectId = null) => {
    console.log('🔍 Loading project data for project management...');

    try {
      // Load user data from multiple sources for accuracy
      let userData = null;
      try {
        const apiUserData = await realApiService.users.getCurrentUser();
        console.log('✅ User data from API:', apiUserData);
        userData = apiUserData;
      } catch (apiError) {
        console.warn('⚠️ Failed to load user from API, falling back to auth service:', apiError);
        const authService = (await import('../../utils/authService')).default;
        userData = await authService.getCurrentUser();
        console.log('✅ User data from auth service:', userData);
      }

      if (!userData) {
        throw new Error('Failed to load user data');
      }

      // Normalize user data
      const normalizedUser = {
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName || userData.first_name || 'User',
        lastName: userData.lastName || userData.last_name || '',
        role: userData.role || 'member',
        displayName: userData.displayName || `${userData.firstName || userData.first_name || 'User'} ${userData.lastName || userData.last_name || ''}`.trim(),
        jobTitle: userData.jobTitle || userData.job_title || '',
        avatar: userData.avatar || userData.avatar_url || null
      };

      setUser(normalizedUser);
      setUserRole(normalizedUser.role);

      // Load organization data
      const sessionService = (await import('../../utils/sessionService')).default;
      const orgData = sessionService.getCurrentOrganization();
      if (orgData) {
        setCurrentOrganization(orgData);
        console.log('Organization loaded:', orgData);
      }

      // Load project data with enhanced URL management
      let projectData = null;
      let currentProjectId = forceProjectId || searchParams.get('id') || latestProjectId.current;

      // Ensure we have a project ID in the URL
      if (!currentProjectId && orgData?.id) {
        console.log('🔗 No project ID in URL, ensuring project ID...');
        currentProjectId = await urlStateService.ensureProjectId(realApiService, orgData.id);
        if (currentProjectId) {
          setProjectId(currentProjectId);
        }
      }

      if (currentProjectId) {
        try {
          const result = await realApiService.projects.getById(currentProjectId);
          if (result && (result.data || result.name)) {
            projectData = result.data || result;
            console.log('✅ Project loaded successfully:', projectData);

            // Ensure URL is up to date
            if (urlStateService.getProjectId() !== currentProjectId) {
              urlStateService.setProjectId(currentProjectId, { silent: true });
            }
          }
        } catch (projectError) {
          console.log('⚠️ Failed to fetch specific project, finding available projects...', projectError);

          // Try to get first available project as fallback
          if (orgData?.id) {
            try {
              const projects = await realApiService.projects.getAll(orgData.id);
              if (projects && projects.length > 0) {
                projectData = projects[0];
                currentProjectId = projectData.id;
                console.log('✅ Using first available project as fallback:', projectData);

                // Update URL and state
                urlStateService.setProjectId(currentProjectId, { silent: true });
                setProjectId(currentProjectId);
              }
            } catch (fallbackError) {
              console.error('❌ Failed to load fallback projects:', fallbackError);
            }
          }
        }
      }

      if (projectData) {
        setProject(projectData);
        // Use the latest specific tab request if available
        const currentTab = latestTabFromUrl.current;
        if (currentTab && ['overview', 'tasks', 'team', 'settings'].includes(currentTab)) {
          setActiveTab(currentTab);
        }
        return projectData;
      } else {
        // Check if user has permission to create projects
        const hasCreatePermission = canCreateProjects;
        const errorMessage = hasCreatePermission
          ? 'No projects found. Create your first project to get started.'
          : 'No projects found. Contact your organization owner to create projects or grant you project creation permissions.';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error loading project data:', error);
      throw error;
    }
  }, [projectId, canCreateProjects, searchParams]); // Added searchParams to dependencies to prevent stale closures during project switching

  // React to URL parameter changes using React Router hooks
  useEffect(() => {
    const newProjectId = searchParams.get('id');
    const newTab = searchParams.get('tab');

    console.log('🔗 URL parameters changed via React Router:', {
      newProjectId,
      newTab,
      currentProjectId: projectId,
      currentTab: tabFromUrl
    });

    // Only update if values actually changed to prevent infinite loops
    if (newProjectId && newProjectId !== projectId) {
      console.log('🔄 Project ID changed, updating:', projectId, '->', newProjectId);
      setProjectId(newProjectId);
      setProject(null); // Clear current project to force reload
      setError(null);
      dataLoadingService.invalidateCache(componentId);
    }

    // Only update tab if it changed and is valid
    if (newTab && newTab !== tabFromUrl && ['overview', 'tasks', 'team', 'settings'].includes(newTab)) {
      console.log('🔄 Tab changed, updating:', tabFromUrl, '->', newTab);
      setTabFromUrl(newTab);
      setActiveTab(newTab);
    }
  }, [searchParams.get('id'), searchParams.get('tab')]); // Only react to actual parameter changes

  // Listen for project updates from other components
  useEffect(() => {
    const handleProjectUpdated = (event) => {
      const { projectId: updatedProjectId, project: updatedProject } = event.detail;
      
      // If the current project was updated, refresh our local data
      if (updatedProjectId === projectId || updatedProject?.id === projectId) {
        console.log('🔄 Project update detected in ProjectManagement, refreshing data...');
        
        // We can update state directly if the project object is provided and complete
        if (updatedProject && updatedProject.name) {
          setProject(updatedProject);
        }
        
        // Also trigger a full load to ensure everything is in sync
        loadProjectData(projectId);
      }
    };

    window.addEventListener('projectUpdated', handleProjectUpdated);
    window.addEventListener('projectChanged', handleProjectUpdated);
    return () => {
      window.removeEventListener('projectUpdated', handleProjectUpdated);
      window.removeEventListener('projectChanged', handleProjectUpdated);
    };
  }, [projectId, loadProjectData]);

  // Initialize URL state service and register with data loading service
  useEffect(() => {
    console.log('🚀 ProjectManagement component mounted, initializing services...');

    // Initialize URL state service
    urlStateService.initialize();

    // Set up URL state listener
    const removeUrlListener = urlStateService.addListener((type, data) => {
      console.log('🔗 URL state changed in ProjectManagement:', type, data);

      const newProjectId = urlStateService.getProjectId();
      const newTab = urlStateService.getTab();

      // Update local state if URL changed
      if (newProjectId !== projectId) {
        console.log('🔗 Project ID changed in URL:', projectId, '->', newProjectId);
        setProjectId(newProjectId);
      }

      if (newTab !== tabFromUrl) {
        console.log('🔗 Tab changed in URL:', tabFromUrl, '->', newTab);
        setTabFromUrl(newTab);
        setActiveTab(newTab);
      }
    });

    // Register component with data loading service
    dataLoadingService.registerComponent(componentId, loadProjectData, [projectId, tabFromUrl]);

    // Set up logout listener
    const removeLogoutListener = logoutService.addLogoutListener(() => {
      console.log('🔐 Logout detected in ProjectManagement, cleaning up...');
      dataLoadingService.unregisterComponent(componentId);
    });

    // Load data immediately
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        await dataLoadingService.loadData(componentId, true);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load project data:', error);
        setError(error.message || 'Failed to load project data');
        setIsLoading(false);
      }
    };

    loadData();

    // Cleanup function
    return () => {
      console.log('🧹 ProjectManagement component unmounting, cleaning up...');
      dataLoadingService.unregisterComponent(componentId);
      removeLogoutListener();
      removeUrlListener();
    };
  }, [projectId, tabFromUrl, loadProjectData]);

  // Handle tab changes with URL updates
  const handleTabChange = useCallback((newTab) => {
    // Prevent unnecessary updates if already on this tab
    if (newTab === activeTab) {
      return;
    }

    console.log('🔗 Changing tab:', activeTab, '->', newTab);

    // Update local state immediately for responsive UI
    setActiveTab(newTab);
    setTabFromUrl(newTab);
    
    // Update URL state silently to avoid re-triggering
    urlStateService.setTab(newTab, { silent: true });
  }, [activeTab]);

  // Handle URL parameter changes for immediate loading (consolidated with URL change handler above)

  // Direct project reload when project ID changes
  useEffect(() => {
    if (projectId && (!project || project.id !== projectId)) {
      console.log('🔄 Project ID changed, directly reloading project data...', projectId);

      const reloadProject = async () => {
        setIsLoading(true);
        setError(null);

        try {
          await loadProjectData(projectId);
        } catch (error) {
          console.error('Failed to reload project:', error);
          setError(error.message || 'Failed to load project');
        } finally {
          setIsLoading(false);
        }
      };

      reloadProject();
    }
  }, [projectId, project?.id, loadProjectData, project]); // Missing project added to dependencies

  // Legacy useEffect for backward compatibility (will be removed by data loading service)
  // Legacy useEffect removed to prevent infinite loops - data loading is now handled by the consolidated URL change handler above

  // Handle tab changes from URL (consolidated with URL change handler above)


  const handleScheduleMeeting = () => {
    setShowMeetingScheduler(true);
  };

  const handleInviteTeam = () => {
    setShowInviteModal(true);
  };

  // Invite handlers
  const handleOwnerInvite = async (inviteData) => {
    if (!currentOrganization) {
      console.error('No organization found');
      return;
    }

    try {
      console.log('Owner inviting member:', inviteData);
      const teamService = (await import('../../utils/teamService')).default;

      await teamService.inviteTeamMember(currentOrganization.id, {
        email: inviteData.email,
        role: inviteData.role,
        organization_id: currentOrganization.id,
        project_id: project?.id,
      });

      setShowInviteModal(false);
      // Show success message or refresh team data
      console.log('Invitation sent successfully');
    } catch (error) {
      console.error('Failed to send invitation:', error);
    }
  };

  const handleAdminInvite = async (inviteData) => {
    if (!currentOrganization) {
      console.error('No organization found');
      return;
    }

    try {
      console.log('Admin inviting members:', inviteData);
      const teamService = (await import('../../utils/teamService')).default;

      // Send invitations through API
      for (const email of inviteData.emails) {
        await teamService.inviteTeamMember(currentOrganization.id, {
          email,
          role: inviteData.role,
          message: inviteData.welcomeMessage,
          project_id: project?.id,
        });
      }

      setShowInviteModal(false);
      // Show success message or refresh team data
      console.log('Invitations sent successfully');
    } catch (error) {
      console.error('Failed to send invitations:', error);
    }
  };


  const handleExportData = async () => {
    try {
      showToast('Preparing comprehensive project data export...', 'info');
      
      // 1. Fetch deep data (Tasks, Team, etc.)
      const [tasks, teamMembers] = await Promise.all([
        realApiService.cards.getByProject(project.id).catch(() => []),
        realApiService.projects.getTeamMembers(project.id, project.organization_id || currentOrganization?.id).catch(() => []),
      ]);
      // Removed boards fetch (unused)

      // 2. Prepare Statistics for Export
      const totalTasks = tasks.length;
      const completedTasks = tasks.filter(t => t.status === 'done' || t.status === 'completed').length;
      // Removed unused tasksByStatus calculation
      
      const progress = totalTasks > 0 ? Math.round(tasks.reduce((sum, t) => sum + getProgressFromStatus(t.status), 0) / totalTasks) : 0;
      const exportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
      });

      // 3. Prepare display data (Map assignees to full names)
      const memberMap = teamMembers.reduce((acc, m) => {
        acc[m.id] = m;
        return acc;
      }, {});

      const enrichedTasks = tasks.map(task => ({
        ...task,
        assigneeDetails: task.assignees?.map(id => memberMap[id]).filter(Boolean) || []
      }));

      // 4. Generate Comprehensive HTML Export
      const exportHtml = generateProjectExportHtml({
        project,
        currentOrganization,
        user,
        stats: {
          totalTasks,
          completedTasks,
          progress,
          teamSize: teamMembers.length
        },
        enrichedTasks,
        teamMembers
      });

      // 5. Download and Open
      const blob = new Blob([exportHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // We'll open it in a new window to allow direct printing
      const printWindow = window.open(url, '_blank');
      if (!printWindow) {
        // Fallback to download if popup is blocked
        const link = document.createElement('a');
        link.href = url;
        link.download = `Full_Export_${project?.name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast('Export successful! Open the downloaded file to print to PDF.', 'success');
      } else {
        showToast('Full data export prepared! Use the "Print" button to save as PDF.', 'success');
      }
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export comprehensive data:', error);
      showToast('Failed to export comprehensive data.', 'error');
    }
  };

  // Unused handlers commented out to resolve ESLint warnings
  /*
  const handleNewProject = () => {
    navigate('/ai-project-creation');
  };
  */

  const handleBackupProject = async () => {
    try {
      console.log('Creating project backup...');

      if (!project?.id) {
        showToast('No project selected for backup.', 'warning');
        return;
      }

      // Try to get comprehensive project data from API
      let backupData;
      try {
        // Get full project data including tasks, boards, and team members
        const [projectData, projectBoards, projectCards] = await Promise.all([
          realApiService.projects.getById(project.id),
          realApiService.boards.getByProject(project.id).catch(() => []),
          realApiService.cards.getByProject(project.id).catch(() => [])
        ]);

        backupData = {
          project: projectData,
          boards: projectBoards,
          cards: projectCards,
          backupDate: new Date().toISOString(),
          backupType: 'full_backup',
          version: '2.0',
          metadata: {
            exportedBy: 'Agno WorkSphere',
            projectId: project.id,
            projectName: project.name
          }
        };

        console.log('✅ Comprehensive backup data prepared:', backupData);
      } catch (apiError) {
        console.warn('API backup failed, using basic project data:', apiError.message);

        // Fallback to basic project data
        backupData = {
          project: project,
          backupDate: new Date().toISOString(),
          backupType: 'basic_backup',
          version: '1.0',
          metadata: {
            exportedBy: 'Agno WorkSphere',
            projectId: project.id,
            projectName: project.name,
            note: 'Basic backup - API data not available'
          }
        };
      }

      // Create and download backup file
      const dataStr = JSON.stringify(backupData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project?.name || 'project'}_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Project backup created successfully');
      showToast('Project backup created and downloaded successfully!', 'success');
    } catch (error) {
      console.error('Failed to create backup:', error);
      showToast('Failed to create project backup. Please try again.', 'error');
    }
  };

  const handleArchiveProject = async () => {
    try {
      if (!project?.id) {
        showToast('No project selected for archiving.', 'warning');
        return;
      }

      if (!window.confirm(`Are you sure you want to archive "${project.name}"? You can restore it later from the archived projects section.`)) {
        return;
      }

      console.log('Archiving project:', project.id);

      await realApiService.projects.update(project.id, {
        status: 'archived',
        archived_at: new Date().toISOString()
      });

      console.log('✅ Project archived successfully');
      showToast('Project archived successfully! You can find it in the archived projects section.', 'success');

      // Redirect to dashboard
      navigate('/role-based-dashboard');
    } catch (error) {
      console.error('Failed to archive project:', error);
      showToast('Failed to archive project. Please try again.', 'error');
    }
  };



  if (isLoading)
    return <div className='p-8 text-center'>Loading project management...</div>;

  if (error) {
    // Check if this is a "no projects found" error
    const isNoProjectsError = error.includes('No projects found');

    if (isNoProjectsError) {
      return (
        <div className='min-h-screen bg-background'>
          <RoleBasedHeader currentUser={user} userRole={userRole} />
          <main className='pt-16'>
            <div className='max-w-4xl mx-auto py-16 px-4'>
              <div className='text-center'>
                <Icon name='FolderPlus' size={64} className='mx-auto mb-6 text-slate-400' />
                <h1 className='text-3xl font-bold text-slate-900 mb-4'>
                  No Projects Found
                </h1>
                <p className='text-lg text-slate-600 mb-8 max-w-2xl mx-auto'>
                  You don't have any projects yet. Create your first project to get started with project management.
                </p>

                <div className="space-y-4">
                  {/* Header */}
      {canCreateProjects ? (
                    <Button
                      size='lg'
                      onClick={() => navigate('/role-based-dashboard')}
                      className='mr-4'
                    >
                      <Icon name='Plus' size={20} className='mr-2' />
                      Create Your First Project
                    </Button>
                  ) : (
                    <div className='bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto'>
                      <p className='text-amber-800 text-sm'>
                        You don't have permission to create projects. Contact your organization owner to create projects or grant you project creation permissions.
                      </p>
                    </div>
                  )}

                  <Button
                    variant='outline'
                    onClick={() => navigate('/role-based-dashboard')}
                  >
                    <Icon name='ArrowLeft' size={16} className='mr-2' />
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </main>
        </div>
      );
    }

    // For other errors, show the original error display
    return (
      <div className='min-h-screen bg-background'>
        <RoleBasedHeader currentUser={user} userRole={userRole} />
        <main className='pt-16'>
          <div className='max-w-4xl mx-auto py-16 px-4'>
            <div className='text-center'>
              <Icon name='AlertCircle' size={64} className='mx-auto mb-6 text-red-400' />
              <h1 className='text-3xl font-bold text-slate-900 mb-4'>
                Error Loading Project
              </h1>
              <p className='text-lg text-slate-600 mb-8'>
                {error}
              </p>
              <div className='space-y-4'>
                <Button onClick={() => window.location.reload()}>
                  <Icon name='RefreshCw' size={16} className='mr-2' />
                  Try Again
                </Button>
                <Button
                  variant='outline'
                  onClick={() => navigate('/role-based-dashboard')}
                >
                  <Icon name='ArrowLeft' size={16} className='mr-2' />
                  Back to Dashboard
                </Button>
              </div>
              <div className='mt-8 text-xs text-gray-500'>
                Project ID: <span className='font-mono'>{projectId || '(none)'}</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const ActiveTab = tabs.find((t) => t.id === activeTab);
  const ActiveComponent = ActiveTab?.component;

  return (
    <div className='min-h-screen bg-background'>
      <RoleBasedHeader currentUser={user} userRole={userRole} />
      <main className='pt-16'>
        <div className='max-w-[1600px] mx-auto py-8 px-10'>
          <Breadcrumb projectName={project?.name} />

        {/* Page Header */}
        <div className='flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8'>
          <div className='flex-1'>
            <h1 className='text-3xl font-bold text-slate-900 mb-2'>
              {project?.name || 'Project Management'}
            </h1>
            <p className='text-slate-600 text-base leading-relaxed'>
              {project?.description ||
                'Project management and team coordination'}
            </p>
          </div>
          <div className='flex flex-wrap gap-3'>
            <Button
              variant='outline'
              iconName='Download'
              iconPosition='left'
              className='whitespace-nowrap'
              onClick={handleExportData}
            >
              Export Data
            </Button>

          </div>
        </div>

        {/* Tab Navigation */}
        <div className='bg-white rounded-lg border border-slate-200 mb-6 shadow-sm'>
          {/* Desktop Tab Navigation */}
          <div className='hidden md:flex border-b border-slate-200'>
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 font-medium transition-all relative ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary bg-primary-light/50'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon name={tab.icon} size={18} />
                <span className='text-sm'>{tab.label}</span>
              </button>
            ))}
          </div>
          {/* Mobile Tab Navigation */}
          <div className='md:hidden border-b border-border p-4'>
            <div className='relative'>
              <select
                value={activeTab}
                onChange={(e) => handleTabChange(e.target.value)}
                className='w-full appearance-none bg-background border border-border rounded-lg px-4 py-3 pr-10 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
              >
                {visibleTabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.label} - {tab.description}
                  </option>
                ))}
              </select>
              <Icon
                name='ChevronDown'
                size={20}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary pointer-events-none'
              />
            </div>
          </div>
          {/* Tab Content */}
          <div className='p-6'>
            {/* Active Tab Header (Mobile) */}
            <div className='flex items-center gap-3 mb-6 md:hidden'>
              <Icon name={ActiveTab.icon} size={24} className='text-primary' />
              <div>
                <h2 className='text-xl font-semibold text-foreground'>
                  {ActiveTab.label}
                </h2>
                <p className='text-sm text-text-secondary'>
                  {ActiveTab.description}
                </p>
              </div>
            </div>
            {/* Render Active Tab Content */}
            <Suspense fallback={
              <div className="flex flex-col items-center justify-center space-y-4 py-8">
                <Icon name='Loader2' size={32} className='animate-spin text-primary' />
                <div className="text-lg text-muted-foreground">
                  Loading tab...
                </div>
              </div>
            }>
              {ActiveComponent && <ActiveComponent project={project} userRole={userRole} currentOrganization={currentOrganization} onSwitchTab={handleTabChange} />}
            </Suspense>
          </div>
        </div>

      </div>

      {/* Meeting Scheduler Modal */}
      {showMeetingScheduler && (
        <Suspense fallback={<div>Loading meeting scheduler...</div>}>
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Schedule Meeting</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowMeetingScheduler(false)}
                  iconName="X"
                />
              </div>
              <div className="p-4">
                <MeetingScheduler
                  projectData={project}
                  organizationMembers={project?.team || []}
                  onClose={() => setShowMeetingScheduler(false)}
                />
              </div>
            </div>
          </div>
        </Suspense>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && userRole === 'owner' && (
        <OwnerInviteMemberModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleOwnerInvite}
          organizationName={currentOrganization?.name || 'Organization'}
        />
      )}

      {showInviteModal && userRole === 'admin' && (
        <AdminInviteMemberModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleAdminInvite}
          organizationName={currentOrganization?.name || 'Organization'}
        />
      )}
      </main>
    </div>
  );
};

export default ProjectManagement;
